import { Button, Spin } from 'antd';
import { getDownloadURL, uploadBytes } from 'firebase/storage';
import React, { useContext, useImperativeHandle, useState } from 'react';
import Webcam from 'react-webcam';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import Modal from '../../../../components/UI/Modal/Modal';
import { Context } from '../../../../index';

const Option = styled(Button)`
  width: 100%;
  margin-top: 20px;
`;

const OptionUpload = styled.label`
  display: flex;
  width: 100%;
  height: 32px;
  align-items: center;
  justify-content: center;
  background-color: #1890ff;
  color: white;
  cursor: pointer;

  &:hover {
    background-color: #40a9ff;
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
`;

const BtnsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Preview = styled.img``;

const AvatarUpload = React.forwardRef(({ isLawyer }, ref) => {
  const [uploadOptionIsVisible, setUploadOptionIsVisible] = useState(false);
  const [webcamIsVisible, setWebcamIsVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selfieImage, setSelfieImage] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const { firestore, auth, storage } = useContext(Context);
  const user = auth.currentUser;
  const userId = user?.uid;

  useImperativeHandle(ref, () => ({
    showModal: () => setUploadOptionIsVisible(true),
  }));

  const closeUploadOptionModal = () => setUploadOptionIsVisible(false);
  const showWebcam = () => {
    setUploadOptionIsVisible(false);
    setWebcamIsVisible(true);
  };
  const closeWebcam = () => setWebcamIsVisible(false);

  const uploadImage = (file) => {
    const _file = file || uploadFile;
    if (_file) {
      setUploading(true);
      const extension = _file?.name?.split('.')?.pop();
      const fileName = `${uuidv4()}.${extension}`;
      const ref = storage.ref(`${userId}/${fileName}`);
      uploadBytes(ref, _file)
        .then((snapshot) =>
          getDownloadURL(snapshot.ref).then((downloadURL) => {
            const avatar = {
              avatar_name: fileName,
              avatar_path: downloadURL,
            };
            return firestore
              .collection(isLawyer ? 'lawyer_full_profile' : 'user_full_profile')
              .doc(userId)
              .update({
                avatar,
              });
          }),
        )
        .catch(() => alert('Uploading failed'))
        .finally(() => {
          setUploading(false);
          closeWebcam();
          closeUploadOptionModal();
        });
    }
  };

  const dataUrlToFile = async (dataUrl, fileName) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], fileName, { type: 'image/png' });
  };

  const makePhoto = async (getScreenshot) => {
    const imageSrc = getScreenshot();
    setSelfieImage(imageSrc);
    const file = await dataUrlToFile(imageSrc, 'download.png');
    setUploadFile(file);
  };

  const handleSelectFile = (event) => {
    const file = event.target.files?.[0];
    uploadImage(file);
  };

  const removeImage = () => {
    setUploadFile(null);
    setSelfieImage(null);
  };

  const deleteImage = () => {
    setDeleting(true);
    firestore
      .collection(isLawyer ? 'lawyer_full_profile' : 'user_full_profile')
      .doc(userId)
      .update({
        avatar: null,
      })
      .finally(() => {
        setDeleting(false);
        closeUploadOptionModal();
      });
  };

  return (
    <>
      <Modal isVisible={uploadOptionIsVisible} title="Edit avatar">
        <Options>
          {!uploading ? (
            <OptionUpload type="button" htmlFor="upload">
              Upload from gallery
            </OptionUpload>
          ) : (
            <Spin />
          )}
          <input
            type="file"
            accept="image/png, image/jpeg"
            id="upload"
            onChange={handleSelectFile}
            style={{ display: 'none' }}
          />
          <Option type="primary" onClick={showWebcam}>
            Use a camera
          </Option>
          <Option type="danger" disabled={deleting} onClick={deleteImage}>
            Delete photo
          </Option>
          <Option onClick={closeUploadOptionModal}>Close</Option>
        </Options>
      </Modal>
      <Modal isVisible={webcamIsVisible} title="Please make a photo">
        {selfieImage ? (
          <PreviewWrapper>
            <Preview src={selfieImage} />
            <Option onClick={removeImage}>Back</Option>
            <Option type="primary" disabled={uploading} onClick={() => uploadImage()}>
              Save photo as Avatar
            </Option>
          </PreviewWrapper>
        ) : (
          <Webcam
            width={480}
            height={480}
            screenshotFormat="image/png"
            videoConstraints={{
              width: 480,
              height: 480,
            }}
          >
            {({ getScreenshot }) => (
              <BtnsWrapper>
                <Option type="primary" onClick={() => makePhoto(getScreenshot)}>
                  Make photo
                </Option>
              </BtnsWrapper>
            )}
          </Webcam>
        )}
        <Option onClick={closeWebcam}>Close</Option>
      </Modal>
    </>
  );
});

export default AvatarUpload;
